/**
 * Review: named review links (one per client or round, each with its renders, expiry and comment
 * setting; revoked ones stay as history), the project's finished renders (label, note, final,
 * delete), and reviewers' comments filtered by link or render.
 */

import { Check, Copy, Download, Link2, Pause, Play, Plus, Star, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fileUrl, type LinkRenders, type RenderFile, type ReviewLink } from "../api";
import { appUrl } from "../base";
import { useEditor } from "./store";

type Tab = "links" | "renders" | "comments";
const DAY = 86400;

export function ShareDialog({ tab: first = "links" }: { tab?: Tab }) {
  const project = useEditor((s) => s.project)!;
  const [tab, setTab] = useState<Tab>(first);
  const [links, setLinks] = useState<ReviewLink[]>([]);
  const [renders, setRenders] = useState<{ renders: RenderFile[]; totalSize: number }>({ renders: [], totalSize: 0 });
  const [commentLink, setCommentLink] = useState("");
  const [err, setErr] = useState("");
  const close = () => useEditor.getState().set({ panel: null });

  const load = useCallback(async () => {
    try {
      const [l, r] = await Promise.all([api.reviewLinks(project.id), api.renders(project.id)]);
      setLinks(l.links);
      setRenders(r);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [project.id]);
  useEffect(() => void load(), [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setErr("");
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    }
    await load();
    window.dispatchEvent(new Event("studio:poll"));
  };

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal share" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Review</h3>
          <div className="tabs">
            <button className={tab === "links" ? "on" : ""} onClick={() => setTab("links")}>Links ({links.filter((l) => l.status === "active").length})</button>
            <button className={tab === "renders" ? "on" : ""} onClick={() => setTab("renders")}>Renders ({renders.renders.length})</button>
            <button className={tab === "comments" ? "on" : ""} onClick={() => setTab("comments")}>Comments</button>
          </div>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        {err && <p className="bad">{err}</p>}
        {tab === "links" && (
          <Links links={links} renders={renders.renders} act={act} showComments={(lid) => { setCommentLink(lid); setTab("comments"); }} />
        )}
        {tab === "renders" && <Renders data={renders} act={act} />}
        {tab === "comments" && <Comments links={links} renders={renders.renders} link={commentLink} setLink={setCommentLink} />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------ links

function Links({ links, renders, act, showComments }: {
  links: ReviewLink[];
  renders: RenderFile[];
  act: (fn: () => Promise<unknown>) => Promise<void>;
  showComments: (lid: string) => void;
}) {
  const project = useEditor((s) => s.project)!;
  const [editing, setEditing] = useState<ReviewLink | "new" | null>(links.length ? null : "new");
  const live = links.filter((l) => l.status === "active" || l.status === "paused");
  const old = links.filter((l) => l.status === "expired" || l.status === "revoked");

  return (
    <section>
      <p className="muted small">
        Anyone with a link can watch the renders it shows (with captions) and leave comments pinned to a moment; they can't open the editor or see
        anything else. Make one link per client or review round, so you can tell their comments apart and turn each off on its own.
      </p>
      {editing ? (
        <LinkForm link={editing === "new" ? null : editing} renders={renders} onCancel={() => setEditing(null)} onSave={async (body) => {
          await act(() => (editing === "new" ? api.createLink(project.id, body) : api.updateLink(project.id, editing.id, body)));
          setEditing(null);
        }} />
      ) : (
        <button className="primary" onClick={() => setEditing("new")}><Plus size={14} /> New review link</button>
      )}
      {renders.length === 0 && <p className="muted small">No finished render yet: share a live preview, or render the project first.</p>}
      <div className="link-list">
        {live.map((l) => <LinkRow key={l.id} link={l} act={act} onEdit={() => setEditing(l)} showComments={showComments} />)}
      </div>
      {old.length > 0 && (
        <details className="link-history">
          <summary>History: {old.length} expired or revoked link{old.length === 1 ? "" : "s"}</summary>
          <div className="link-list">
            {old.map((l) => <LinkRow key={l.id} link={l} act={act} onEdit={() => setEditing(l)} showComments={showComments} />)}
          </div>
        </details>
      )}
    </section>
  );
}

function shows(l: ReviewLink): string {
  const r = l.renders;
  if (r === "live") return l.follow ? "live preview, following edits" : `live preview of revision ${l.snapshotRevision ?? "?"} (${when(l.snapshot)})`;
  if (r === "latest") return "latest render";
  if (r === "all") return "all renders";
  return `${r.length} chosen render${r.length === 1 ? "" : "s"}`;
}
const when = (t: number | null) => (t ? new Date(t * 1000).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "never");

function LinkRow({ link: l, act, onEdit, showComments }: {
  link: ReviewLink;
  act: (fn: () => Promise<unknown>) => Promise<void>;
  onEdit: () => void;
  showComments: (lid: string) => void;
}) {
  const pid = useEditor((s) => s.project)!.id;
  const [copied, setCopied] = useState(false);
  const url = `${location.origin}${appUrl(l.path)}`;
  const usable = l.status === "active" || l.status === "paused";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // plain-HTTP pages may not get clipboard access: select the text instead
      (document.getElementById(`url-${l.id}`) as HTMLInputElement | null)?.select();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={`link-item ${l.status}`}>
      <div className="link-top">
        <strong>{l.name}</strong>
        <span className={`badge ${l.status}`}>{l.status}</span>
        <span className="muted small">
          {shows(l)} · {l.comments ? "comments on" : "comments off"}
          {l.expires && l.status !== "revoked" ? ` · ${l.status === "expired" ? "expired" : "expires"} ${when(l.expires)}` : ""}
        </span>
      </div>
      {usable && (
        <div className="share-url">
          <input id={`url-${l.id}`} readOnly value={url} onFocus={(e) => e.target.select()} />
          <button onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}</button>
          <a className="button" href={url} target="_blank" rel="noreferrer"><Link2 size={13} /> Open</a>
        </div>
      )}
      <div className="link-top small">
        <span className="muted">
          Created {when(l.created)} · opened {l.views} time{l.views === 1 ? "" : "s"}{l.lastOpened ? `, last ${when(l.lastOpened)}` : ""}
          {l.revoked ? ` · revoked ${when(l.revoked)}` : ""}
        </span>
        <button className="link" onClick={() => showComments(l.id)}>{l.commentCount} comment{l.commentCount === 1 ? "" : "s"}</button>
        <span className="grow" />
        {l.renders === "live" && !l.follow && usable && (
          <button onClick={() => act(() => api.updateLink(pid, l.id, { refreeze: true }))} title="Show reviewers the project as it is now">Update preview</button>
        )}
        {l.status !== "revoked" && <button onClick={onEdit}>Edit</button>}
        {usable && (
          <button onClick={() => act(() => api.updateLink(pid, l.id, { paused: !l.paused }))} title={l.paused ? "Let reviewers in again" : "Stop the link for now; resume it any time"}>
            {l.paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
          </button>
        )}
        {l.status !== "revoked" && (
          <button onClick={() => confirm(`Revoke "${l.name}"? The link stops working for good (its comments stay).`) && act(() => api.updateLink(pid, l.id, { revoke: true }))}>
            Revoke
          </button>
        )}
        {!usable && (
          <button className="icon" title="Remove from the list (its comments stay)" onClick={() => confirm(`Remove "${l.name}" from the history?`) && act(() => api.deleteLink(pid, l.id))}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function LinkForm({ link, renders, onSave, onCancel }: {
  link: ReviewLink | null;
  renders: RenderFile[];
  onSave: (body: { name: string; renders: LinkRenders; expires: number | null; comments: boolean; follow: boolean }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(link?.name ?? "");
  const [mode, setMode] = useState<"latest" | "all" | "live" | "chosen">(link ? (Array.isArray(link.renders) ? "chosen" : link.renders) : renders.length ? "latest" : "live");
  const [follow, setFollow] = useState(!!link?.follow);
  const [chosen, setChosen] = useState<string[]>(Array.isArray(link?.renders) ? link!.renders : renders.slice(0, 1).map((r) => r.file));
  const [expiry, setExpiry] = useState<string>(link?.expires ? "date" : "never");
  const [date, setDate] = useState(() => new Date(((link?.expires ?? Date.now() / 1000 + 7 * DAY) * 1000)).toISOString().slice(0, 10));
  const [comments, setComments] = useState(link?.comments ?? true);
  const [busy, setBusy] = useState(false);
  const expires = (): number | null => {
    if (expiry === "never") return null;
    if (expiry === "date") return new Date(`${date}T23:59:59`).getTime() / 1000;
    return Math.round(Date.now() / 1000 + Number(expiry) * DAY);
  };
  const save = async () => {
    setBusy(true);
    try {
      await onSave({ name: name.trim() || "Review link", renders: mode === "chosen" ? chosen : mode, expires: expires(), comments, follow: mode === "live" && follow });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="link-form">
      <label>Name <input autoFocus value={name} maxLength={80} placeholder="e.g. Acme legal, round 1" onChange={(e) => setName(e.target.value)} /></label>
      <label>Shows
        <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
          <option value="latest">The latest render (updates as you render)</option>
          <option value="all">All renders</option>
          <option value="chosen">Chosen renders…</option>
          <option value="live">Live preview (no render needed)</option>
        </select>
      </label>
      {mode === "live" && (
        <div className="chosen">
          <label className="check small"><input type="radio" checked={!follow} onChange={() => setFollow(false)} /> Frozen: the project as it is now (use “Update preview” to send changes)</label>
          <label className="check small"><input type="radio" checked={follow} onChange={() => setFollow(true)} /> Follow my edits: reviewers always see the latest version</label>
          <span className="muted small">Drawn in the reviewer's browser, so it can stutter on slow devices and may differ slightly from the final video.</span>
        </div>
      )}
      {mode === "chosen" && (
        <div className="chosen">
          {renders.length === 0 && <span className="muted small">No renders yet.</span>}
          {renders.map((r) => (
            <label key={r.file} className="check small">
              <input type="checkbox" checked={chosen.includes(r.file)} onChange={(e) => setChosen(e.target.checked ? [...chosen, r.file] : chosen.filter((f) => f !== r.file))} />
              {renderName(r)}
            </label>
          ))}
        </div>
      )}
      <label>Expires
        <select value={expiry} onChange={(e) => setExpiry(e.target.value)}>
          <option value="never">Never</option>
          <option value="1">In 1 day</option>
          <option value="7">In 7 days</option>
          <option value="30">In 30 days</option>
          <option value="date">On a date…</option>
        </select>
        {expiry === "date" && <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
      </label>
      <label className="check"><input type="checkbox" checked={comments} onChange={(e) => setComments(e.target.checked)} /> Reviewers can comment</label>
      <div className="analysis-actions">
        <button onClick={onCancel}>Cancel</button>
        <button className="primary" disabled={busy || (mode === "chosen" && chosen.length === 0)} onClick={save}>{link ? "Save" : "Create link"}</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------ renders

function renderName(r: RenderFile): string {
  const scope = r.scope === "all" ? "" : ` · ${r.scope}`;
  return `${r.label || new Date(r.created * 1000).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · ${r.quality ?? "?"}${scope}`;
}
function fmtSize(b: number): string {
  return b > 1e9 ? `${(b / 1e9).toFixed(2)} GB` : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} kB`;
}
function fmtLen(s: number | null): string {
  return s == null ? "" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
}

function Renders({ data, act }: { data: { renders: RenderFile[]; totalSize: number }; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const pid = useEditor((s) => s.project)!.id;
  if (data.renders.length === 0) return <section><p className="muted small">No finished renders. Render the project from the Render panel.</p></section>;
  const remove = (r: RenderFile) => {
    const warn = r.links.length ? `\n\nReview link${r.links.length === 1 ? "" : "s"} showing it: ${r.links.join(", ")}. Reviewers will see the next render or "removed".` : "";
    if (confirm(`Delete ${renderName(r)} (${fmtSize(r.size)}) and its caption files?${warn}`)) void act(() => api.deleteRender(pid, r.file));
  };
  return (
    <section>
      <p className="muted small">{data.renders.length} render{data.renders.length === 1 ? "" : "s"} using {fmtSize(data.totalSize)}. Label them so reviewers and teammates can tell them apart; mark the one you delivered as final.</p>
      <div className="render-list">
        {data.renders.map((r) => (
          <div key={r.file} className={`render-item ${r.final ? "final" : ""}`}>
            <button className={`icon star ${r.final ? "on" : ""}`} title={r.final ? "Final render" : "Mark as the final render"}
              onClick={() => act(() => api.updateRender(pid, r.file, { final: !r.final }))}><Star size={16} /></button>
            <div className="body">
              <input className="label" placeholder="Label, e.g. Client cut v2" defaultValue={r.label} maxLength={80}
                onBlur={(e) => e.target.value !== r.label && act(() => api.updateRender(pid, r.file, { label: e.target.value }))} />
              <div className="muted small">
                {new Date(r.created * 1000).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · {r.quality ?? "?"}{r.fps ? ` ${r.fps} fps` : ""}
                {r.scope !== "all" ? ` · ${r.scope} from ${fmtLen(r.start)}` : ""}{r.duration ? ` · ${fmtLen(r.duration)}` : ""} · {fmtSize(r.size)}
                {r.revision != null ? ` · revision ${r.revision}` : ""}
              </div>
              <textarea className="note" placeholder="Note (who it went to, what changed…)" rows={1} defaultValue={r.note} maxLength={1000}
                onBlur={(e) => e.target.value !== r.note && act(() => api.updateRender(pid, r.file, { note: e.target.value }))} />
              {r.links.length > 0 && <div className="small">Shown by: {r.links.map((n) => <span key={n} className="chip">{n}</span>)}</div>}
            </div>
            <div className="actions">
              <a className="button" href={fileUrl(pid, `renders/${r.file}`)} target="_blank" rel="noreferrer" title="Play"><Play size={13} /></a>
              <a className="button" href={fileUrl(pid, `renders/${r.file}`) + "?download=1"} download title="Download MP4"><Download size={13} /></a>
              {r.srt && <a className="button" href={fileUrl(pid, `renders/${r.srt}`) + "?download=1"} download>.srt</a>}
              {r.vtt && <a className="button" href={fileUrl(pid, `renders/${r.vtt}`) + "?download=1"} download>.vtt</a>}
              <button className="icon" title="Delete this render" onClick={() => remove(r)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------------------------ comments

function Comments({ links, renders, link, setLink }: { links: ReviewLink[]; renders: RenderFile[]; link: string; setLink: (v: string) => void }) {
  const project = useEditor((s) => s.project)!;
  const raw = useEditor((s) => s.comments);
  const [render, setRender] = useState("");
  const [open, setOpen] = useState(false);
  const linkName = useMemo(() => new Map(links.map((l) => [l.id, l.name])), [links]);
  const renderLabel = useMemo(() => new Map(renders.map((r) => [r.file, renderName(r)])), [renders]);
  const comments = useMemo(
    () => [...raw].filter((c) => (!link || c.link === link) && (!render || c.render === render) && (!open || !c.resolved)).sort((a, b) => a.t - b.t),
    [raw, link, render, open],
  );
  const usedRenders = [...new Set(raw.map((c) => c.render).filter(Boolean))] as string[];
  const refresh = () => window.dispatchEvent(new Event("studio:poll"));
  const seek = (t: number) => window.dispatchEvent(new CustomEvent("studio:seek", { detail: t }));
  return (
    <section>
      <div className="comment-filters">
        <select value={link} onChange={(e) => setLink(e.target.value)}>
          <option value="">All links</option>
          {links.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          {raw.some((c) => !linkName.has(c.link)) && <option value="l-first">Removed or older link</option>}
        </select>
        <select value={render} onChange={(e) => setRender(e.target.value)}>
          <option value="">Any render</option>
          {usedRenders.map((f) => <option key={f} value={f}>{renderLabel.get(f) ?? `${f} (deleted)`}</option>)}
        </select>
        <label className="check small"><input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} /> Open only</label>
        <span className="muted small">{comments.length} of {raw.length}</span>
      </div>
      {comments.length === 0 && <p className="muted small">None{raw.length ? " match" : " yet. Comments show up here and on the timeline's Comments row"}.</p>}
      <div className="comment-list">
        {comments.map((c) => (
          <div key={c.id} className={`comment-item ${c.resolved ? "resolved" : ""}`}>
            <button className="link when" onClick={() => seek(c.t)} title="Jump the playhead here">{Math.floor(c.t / 60)}:{String(Math.floor(c.t % 60)).padStart(2, "0")}</button>
            <div className="body">
              <strong>{c.name}</strong> <span className="muted small">{new Date(c.created * 1000).toLocaleString()}</span>
              <div className="muted small">
                via {linkName.get(c.link) ?? "a removed link"}{c.render ? ` · watching ${renderLabel.get(c.render) ?? "a deleted render"}` : ""}
              </div>
              <div className="text">{c.text}</div>
            </div>
            <label className="small" title="Mark as dealt with">
              <input type="checkbox" checked={c.resolved} onChange={(e) => {
                const v = e.target.checked;
                // show it at once; the server confirms on the next poll
                useEditor.setState({ comments: useEditor.getState().comments.map((x) => (x.id === c.id ? { ...x, resolved: v } : x)) });
                void api.resolveComment(project.id, c.id, v).then(refresh);
              }} /> Done
            </label>
            <button className="icon" title="Delete" onClick={() => {
              useEditor.setState({ comments: useEditor.getState().comments.filter((x) => x.id !== c.id) });
              void api.deleteComment(project.id, c.id).then(refresh);
            }}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </section>
  );
}
